import React, { useEffect, useState, useRef, useCallback } from 'react';
import GoogleMapTracker from '../../components/GoogleMapTracker';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';
import SAR from '../../../components/SAR';
import { useDialog } from '../../../context/DialogContext';
import useWs from '../../../hooks/useWs';
import CheckoutSheet from '../wallet/CheckoutSheet';


const fmtSAR = v => Number(v).toFixed(2);
const ACTIVE = ['assigned','accepted','started','arrived','en_route_pickup','at_source','loading','en_route_delivery','delivering'];

const DRIVER_TO_CUSTOMER = {
  en_route_pickup:   'accepted',
  at_source:         'accepted',
  loading:           'accepted',
  en_route_delivery: 'accepted',
  arrived:           'arrived',
  delivering:        'arrived',
};

const STEP_ICONS = {
  pending:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:'1em',height:'1em'}}><path strokeLinecap="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>,
  confirmed: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{width:'1em',height:'1em'}}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>,
  assigned:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:'1em',height:'1em'}}><path strokeLinecap="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0"/></svg>,
  accepted:  <svg viewBox="0 0 24 24" fill="currentColor" style={{width:'1em',height:'1em'}}><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>,
  arrived:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:'1em',height:'1em'}}><path strokeLinecap="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"/></svg>,
  completed: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:'1em',height:'1em'}}><path strokeLinecap="round" d="M12 2C9 5.5 5 9.3 5 13.5a7 7 0 0014 0C19 9.3 15 5.5 12 2z"/></svg>,
};

const STATUS_AR = {
  pending:             'في انتظار السائق',
  confirmed:           'تم التأكيد',
  assigned:            'تم تعيين السائق',
  accepted:            'السائق في الطريق',
  started:             'السائق في الطريق',
  arrived:             'وصل السائق',
  delivering:          'جاري التوصيل',
  en_route_pickup:     'في الطريق للمصدر',
  at_source:           'في المصدر',
  loading:             'جاري التحميل',
  en_route_delivery:   'في الطريق إليك',
  completed:           'تم التسليم',
  cancelled:           'ملغى',
  no_driver_available: 'لا يوجد سائق متاح',
  collecting_offers:   'جمع العروض',
  offers_ready:        'العروض جاهزة',
  expired_no_offers:   'انتهت مهلة العروض',
};

const TIMELINE_STEPS = [
  { status: 'pending',   label: 'تم إنشاء الطلب',  key: 'requested_at' },
  { status: 'confirmed', label: 'تم التأكيد',        key: 'confirmed_at' },
  { status: 'assigned',  label: 'تم تعيين السائق',  key: 'assigned_at' },
  { status: 'accepted',  label: 'السائق في الطريق', key: 'accepted_at' },
  { status: 'arrived',   label: 'وصل السائق',        key: 'arrived_at' },
  { status: 'completed', label: 'تم التسليم',        key: 'completed_at' },
];

const STATUS_CLS = {
  pending:   'bg-amber-100 text-amber-700 border-amber-200',
  confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
  assigned:  'bg-indigo-100 text-indigo-700 border-indigo-200',
  accepted:  'bg-cyan-100 text-cyan-700 border-cyan-200',
  started:   'bg-cyan-100 text-cyan-700 border-cyan-200',
  arrived:   'bg-teal-100 text-teal-700 border-teal-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  cancelled:           'bg-red-100 text-red-700 border-red-200',
  no_driver_available:  'bg-orange-100 text-orange-700 border-orange-200',
  collecting_offers:     'bg-sky-100 text-sky-700 border-sky-200',
  offers_ready:          'bg-emerald-100 text-emerald-700 border-emerald-200',
  expired_no_offers:     'bg-red-100 text-red-700 border-red-200',
};

const getPmIcon = (type, isDark) => ({
  cash:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:'1.25rem',height:'1.25rem'}}><path strokeLinecap="round" d="M2 7h20M2 12h20M6 17h12"/><rect x="2" y="5" width="20" height="14" rx="2"/></svg>,
  card:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:'1.25rem',height:'1.25rem'}}><rect x="2" y="5" width="20" height="14" rx="2"/><path strokeLinecap="round" d="M2 10h20M6 15h3"/></svg>,
  wallet: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} style={{width:'1.25rem',height:'1.25rem'}} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 12a1 1 0 100 2 1 1 0 000-2z" fill="currentColor" stroke="none"/><path d="M2 9h20"/></svg>,
})[type];

const PM_OPTIONS = [
  { value: 'cash',   label: 'نقداً',         note: 'يُسلَّم للسائق عند التسليم' },
  { value: 'card',   label: 'دفع إلكتروني', note: 'فيزا · ماستركارد · مدى · آبل باي' },
  { value: 'wallet', label: 'المحفظة',       note: 'يُخصم من رصيد محفظتك' },
];

function BidProgressBar({ expiresAt }) {
  const [pct, setPct] = useState(100);
  useEffect(() => {
    const end = new Date(expiresAt).getTime();
    const update = () => {
      const now = Date.now();
      const total = 30 * 1000; // 30s window
      const remaining = Math.max(0, end - now);
      setPct(Math.round((remaining / total) * 100));
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return (
    <div
      className="h-full rounded-full bg-sky-500 transition-all duration-1000"
      style={{ width: `${pct}%` }}
    />
  );
}

export default function OrderDetailPage() {
  const { id }        = useParams();
  const { customer, apiCall } = useCustomerAuth();
  const { confirm } = useDialog();
  const toast         = useToast();
  const navigate      = useNavigate();
  const pollRef       = useRef(null);
  const mapRef        = useRef(null);

  const [isDark, setIsDark] = useState(() => document.documentElement.getAttribute('data-theme') !== 'light');
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute('data-theme') !== 'light')
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const [order,          setOrder]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [rating,         setRating]         = useState(0);
  const [review,         setReview]         = useState('');
  const [ratingLoading,  setRatingLoading]  = useState(false);
  const [showThankYou,   setShowThankYou]   = useState(false);
  const [cancelling,     setCancelling]     = useState(false);
  const [payMethod,      setPayMethod]      = useState(null);
  const [savingPay,      setSavingPay]      = useState(false);
  const [walletBalance,  setWalletBalance]  = useState(null);
  const [checkoutOpen,   setCheckoutOpen]   = useState(false);
  const [alternatives,   setAlternatives]   = useState(null);  // null=not loaded
  const [altLoading,     setAltLoading]     = useState(false);
  const [adjusting,      setAdjusting]      = useState(false);
  const [adjustDone,     setAdjustDone]     = useState(false);
  const [couponInput,    setCouponInput]    = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponRemoving, setCouponRemoving] = useState(false);
  const [suggestedCoupon,setSuggestedCoupon]= useState(null);
  const [bidCountdown,   setBidCountdown]   = useState(null); // seconds remaining
  const [suggestedLoaded,setSuggestedLoaded]= useState(false);

  // Countdown ticker for bid collection window
  useEffect(() => {
    if (!order?.offer_expires_at) { setBidCountdown(null); return; }
    const update = () => {
      const secs = Math.max(0, Math.floor((new Date(order.offer_expires_at) - Date.now()) / 1000));
      setBidCountdown(secs);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [order?.offer_expires_at]);

  const load = async (silent = false) => {
    try {
      const data = await apiCall(`/customer/orders/${id}`);
      setOrder(data);
      if (data.customer_rating && !rating) setRating(data.customer_rating);
      if (data.payment_method && !payMethod) setPayMethod(data.payment_method);
    } catch (err) {
      if (!silent) { toast.error(err.message); navigate('/customer/orders'); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(() => load(true), 30000); // fallback only
    return () => clearInterval(pollRef.current);
  }, [id]);

  useEffect(() => {
    apiCall('/customer/wallet').then(r => setWalletBalance(r.balance ?? 0)).catch(() => {});
  }, [apiCall]);

  // Load alternatives when order has no driver available
  // Fetch suggested coupon once order is assigned/accepted
  useEffect(() => {
    if (!order || order.coupon_code || suggestedLoaded) return;
    const eligible = ['pending','confirmed','assigned','accepted','started','en_route_pickup',
                      'at_source','loading','en_route_delivery','arrived','delivering'];
    if (eligible.includes(order.status)) {
      setSuggestedLoaded(true);
      apiCall(`/customer/coupons/order/${id}/suggested`)
        .then(r => { if (r.coupon) setSuggestedCoupon(r.coupon); })
        .catch(() => {});
    }
  }, [order?.status, id, suggestedLoaded, order?.coupon_code, apiCall]);

  useEffect(() => {
    if (['no_driver_available','expired_no_offers'].includes(order?.status) && alternatives === null && !altLoading) {
      setAltLoading(true);
      apiCall(`/customer/orders/${id}/alternatives`)
        .then(r => setAlternatives(r.alternatives || []))
        .catch(() => setAlternatives([]))
        .finally(() => setAltLoading(false));
    }
  }, [order?.status, id, alternatives, altLoading, apiCall]);

  const acceptAlternative = async (qty) => {
    setAdjusting(true);
    try {
      const res = await apiCall(`/customer/orders/${id}/adjust-capacity`, {
        method: 'POST',
        body: JSON.stringify({ new_quantity: qty }),
      });
      setAdjustDone(true);
      setAlternatives(null);
      setTimeout(() => load(true), 2000); // reload after dispatch starts
      toast.success(res.message || 'تم تعديل الطلب بنجاح');
    } catch (err) {
      toast.error(err.message || 'تعذر تعديل الطلب');
    } finally {
      setAdjusting(false);
    }
  };

  // WS: instant update on any order event
  const wsToken = localStorage.getItem('watrly_customer_token') || '';
  useWs(`/order/${id}`, useCallback((data) => {
    load(true);
    if (data.type === 'order_cancelled' || data.status === 'cancelled') {
      toast.warning('تم إلغاء طلبك تلقائياً — لم يُعثر على سائق في الوقت المحدد');
    } else if (data.status === 'no_driver_available') {
      toast.warning('لا يوجد سائق متاح حالياً — سيتم إعادة المحاولة أو التعيين يدوياً');
    } else if (data.status === 'offers_ready') {
      toast.success('✅ اكتملت العروض — يمكنك الاختيار الآن!');
    } else if (data.status === 'expired_no_offers') {
      toast.warning('لم يتقدم أي سائق — يمكنك إلغاء الطلب أو الانتظار');
    } else if (data.type === 'new_bid') {
      toast.success(`🏷️ وصل عرض جديد من ${data.ship_name || 'مزود'} — اضغط لعرض العروض`);
    } else if (data.type === 'order_confirmed') {
      toast.success('✅ تم تأكيد طلبك — السائق في الطريق إليك');
    }
  }, [id]), wsToken);

  const submitRating = async () => {
    if (!rating) { toast.warning('اختر عدد النجوم أولاً'); return; }
    setRatingLoading(true);
    try {
      await apiCall(`/customer/orders/${id}/rate`, {
        method: 'POST',
        body: JSON.stringify({ rating, review: review || null }),
      });
      setShowThankYou(true);
      setTimeout(() => { setShowThankYou(false); navigate('/customer/home'); }, 3000);
    } catch (err) { toast.error(err.message); }
    finally { setRatingLoading(false); }
  };

  const cancelOrder = async () => {
    if (!await confirm({ title: 'إلغاء الطلب', message: 'هل أنت متأكد من إلغاء الطلب؟', type: 'warning', confirmText: 'إلغاء الطلب', cancelText: 'تراجع' })) return;
    setCancelling(true);
    try {
      await apiCall(`/customer/orders/${id}/cancel`, { method: 'POST' });
      toast.success('تم إلغاء الطلب');
      load(true);
    } catch (err) { toast.error(err.message); }
    finally { setCancelling(false); }
  };

  const applyCoupon = async (code) => {
    const c = (code || couponInput).trim().toUpperCase();
    if (!c) return;
    setCouponApplying(true);
    try {
      await apiCall(`/customer/coupons/order/${id}/apply`, {
        method: 'POST',
        body: JSON.stringify({ code: c }),
      });
      setCouponInput('');
      setSuggestedCoupon(null);
      load(true);
      toast.success('تم تطبيق الكوبون بنجاح');
    } catch (err) { toast.error(err.message || 'كود غير صالح'); }
    finally { setCouponApplying(false); }
  };

  const removeCoupon = async () => {
    setCouponRemoving(true);
    try {
      await apiCall(`/customer/coupons/order/${id}/coupon`, { method: 'DELETE' });
      load(true);
      toast.success('تم إزالة الكوبون');
    } catch (err) { toast.error(err.message); }
    finally { setCouponRemoving(false); }
  };

  const savePayment = async (method) => {
    setSavingPay(true);
    setPayMethod(method);
    try {
      await apiCall(`/customer/orders/${id}/payment`, {
        method: 'PUT',
        body: JSON.stringify({ payment_method: method }),
      });
      toast.success('تم تحديث طريقة الدفع');
    } catch { /* ignore */ }
    finally { setSavingPay(false); }
  };

  const fmtDate = d => d
    ? new Date(d).toLocaleString('ar-SA', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  const ThankYouPopup = () => (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'rgba(1,66,115,0.88)', backdropFilter: 'blur(10px)',
    }}>
      <style>{`
        @keyframes popIn { from{transform:scale(0.2) rotate(-10deg);opacity:0} to{transform:scale(1) rotate(0deg);opacity:1} }
        @keyframes floatPart { 0%{transform:translateY(0) rotate(0deg);opacity:1} 100%{transform:translateY(-160px) rotate(540deg);opacity:0} }
        @keyframes cdBar { from{width:0%} to{width:100%} }
        @keyframes starPop { 0%{transform:scale(0)} 70%{transform:scale(1.3)} 100%{transform:scale(1)} }
        .ty-star{display:inline-block;animation:starPop 0.4s ease forwards;}
        .ty-star:nth-child(1){animation-delay:0.05s}
        .ty-star:nth-child(2){animation-delay:0.15s}
        .ty-star:nth-child(3){animation-delay:0.25s}
        .ty-star:nth-child(4){animation-delay:0.35s}
        .ty-star:nth-child(5){animation-delay:0.45s}
      `}</style>
      {[...Array(18)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: i % 3 === 0 ? 12 : 8,
          height: i % 3 === 0 ? 12 : 8,
          borderRadius: i % 2 === 0 ? '50%' : '2px',
          background: ['#FFD700','#FF6B6B','#4ADE80','#60A5FA','#F472B6','#A78BFA'][i % 6],
          top: `${5 + (i * 17 + 13) % 90}%`,
          left: `${5 + (i * 23 + 7) % 90}%`,
          animation: `floatPart ${1.2 + (i % 4) * 0.3}s ease-out ${(i % 6) * 0.1}s infinite`,
        }} />
      ))}
      <div style={{
        background: 'white', borderRadius: 28, padding: '36px 32px',
        textAlign: 'center', maxWidth: 320, width: '90%',
        animation: 'popIn 0.5s cubic-bezier(0.175,0.885,0.32,1.275) forwards',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ fontSize: 72, lineHeight: 1, marginBottom: 16 }}>🌟</div>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: '#014273', marginBottom: 8 }}>شكراً لك!</h2>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
          تقييمك يعني لنا الكثير<br/>ويساعدنا على تقديم خدمة أفضل لك
        </p>
        <div style={{ fontSize: 36, marginBottom: 20 }}>
          {[1,2,3,4,5].map(s => (
            <span key={s} className="ty-star" style={{
              color: s <= rating ? '#FBBF24' : '#e2e8f0',
              fontSize: 36, animationDelay: `${s * 0.1}s`,
            }}>★</span>
          ))}
        </div>
        <div style={{ background: '#f1f5f9', borderRadius: 99, height: 5, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{
            height: '100%', background: 'linear-gradient(90deg,#014273,#0284c7)',
            borderRadius: 99, animation: 'cdBar 3s linear forwards',
          }} />
        </div>
        <p style={{ fontSize: 12, color: '#94a3b8' }}>جاري توجيهك للصفحة الرئيسية...</p>
      </div>
    </div>
  );

  if (loading) return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl animate-pulse" style={{ background: 'var(--wt-surface)' }} />
        <div className="flex-1 space-y-2">
          <div className="h-4 rounded-lg w-28 animate-pulse" style={{ background: 'var(--wt-surface)' }} />
          <div className="h-3 rounded-lg w-20 animate-pulse" style={{ background: 'var(--wt-surface)' }} />
        </div>
      </div>
      {[1,2,3].map(i => <div key={i} className="rounded-2xl h-28 animate-pulse" style={{ background: 'var(--wt-surface)' }} />)}
    </div>
  );
  if (!order) return null;

  const isActive      = ACTIVE.includes(order.status);
  const canCancel     = ['pending','confirmed','no_driver_available','collecting_offers','expired_no_offers'].includes(order.status);
  const isMarketplace  = ['collecting_offers','offers_ready'].includes(order.status);
  const canRate       = order.status === 'completed' && !order.customer_rating;
  const isCompleted   = order.status === 'completed';
  const statusCls     = STATUS_CLS[order.status] || 'bg-gray-100 text-gray-600 border-gray-200';

  /* ── build timeline ── */
  const tsMap = {
    requested_at: order.created_at,
    confirmed_at: order.confirmed_at,
    assigned_at:  order.assigned_at,
    accepted_at:  order.accepted_at,
    arrived_at:   order.arrived_at,
    completed_at: order.completed_at,
  };
  const orderedStatuses = ['pending','confirmed','assigned','accepted','arrived','completed'];
  const customerStatus = DRIVER_TO_CUSTOMER[order.status] || order.status;
  const currentIdx = orderedStatuses.indexOf(customerStatus);
  const timelineSteps = TIMELINE_STEPS.filter(s => s.status !== 'started').map(s => ({
    ...s,
    done: orderedStatuses.indexOf(s.status) <= currentIdx && order.status !== 'cancelled',
    active: s.status === customerStatus && order.status !== 'cancelled',
    time: tsMap[s.key],
  }));

  /* ── map center ── */
  const driverPos = order.driver_lat && order.driver_lng
    ? [order.driver_lat, order.driver_lng] : null;
  const destPos   = order.delivery_lat && order.delivery_lng
    ? [order.delivery_lat, order.delivery_lng] : null;
  const mapCenter = driverPos || destPos || [24.7136, 46.6753];

  /* ── price ── */
  const actTotal = order.actual_total    || 0;
  const estTotal = order.estimated_total || actTotal; // fallback للطلبات القديمة

  return (
    <div className="space-y-4">
      {showThankYou && <ThankYouPopup />}
      {/* ── Header ── */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={() => navigate('/customer/orders')}
          className="w-10 h-10 rounded-2xl flex items-center justify-center transition-all active:scale-95"
          style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)', color: 'var(--wt-text-secondary)' }}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-black" style={{ color: 'var(--wt-text-primary)' }}>{order.order_number}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full"
              style={{
                background: order.status === 'completed' ? 'rgba(16,185,129,0.15)'
                  : order.status === 'cancelled' ? 'rgba(244,63,94,0.12)'
                  : isActive ? 'rgba(10,186,181,0.15)' : 'rgba(255,255,255,0.08)',
                color: order.status === 'completed' ? '#10b981'
                  : order.status === 'cancelled' ? '#f43f5e'
                  : isActive ? '#0ABAB5' : 'var(--wt-text-muted)',
                border: order.status === 'completed' ? '1px solid rgba(16,185,129,0.3)'
                  : order.status === 'cancelled' ? '1px solid rgba(244,63,94,0.25)'
                  : isActive ? '1px solid rgba(10,186,181,0.3)' : '1px solid var(--wt-border)',
              }}
            >
              {STATUS_AR[order.status] || order.status}
            </span>
            {isActive && <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#0ABAB5', boxShadow: '0 0 6px rgba(10,186,181,0.6)' }} />}
          </div>
        </div>
        {canCancel && (
          <button
            onClick={cancelOrder}
            disabled={cancelling}
            className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-xs font-black active:scale-95 transition-all disabled:opacity-40 shrink-0"
            style={{
              background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
              border: 'none',
              color: '#fff',
              boxShadow: '0 3px 14px rgba(244,63,94,0.45)',
            }}
          >
            {cancelling ? (
              <span className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9"/>
                <path d="M15 9l-6 6M9 9l6 6"/>
              </svg>
            )}
            <span>{cancelling ? 'جاري...' : 'إلغاء الطلب'}</span>
          </button>
        )}
      </div>

      {/* ══════ DISPATCHING BANNER ══════ */}
      {order.status === 'pending' && (
        <div className="rounded-2xl px-4 py-3.5 flex items-center gap-3"
          style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.25)' }}>
          <div className="w-5 h-5 border-2 rounded-full animate-spin shrink-0"
            style={{ borderColor: 'rgba(14,165,233,0.3)', borderTopColor: '#0ABAB5' }} />
          <div>
            <p className="text-sm font-bold" style={{ color: '#0ABAB5' }}>جاري البحث عن سائق متاح...</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--wt-text-muted)' }}>سيتم إشعارك فور العثور على سائق</p>
          </div>
        </div>
      )}

      {/* ══════ CONFIRMED BANNER ══════ */}
      {order.status === 'confirmed' && (
        <div className="rounded-2xl px-4 py-3.5 flex items-center gap-3"
          style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.15)' }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
          </span>
          <div>
            <p className="text-sm font-bold" style={{ color: '#10b981' }}>تم تأكيد طلبك</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--wt-text-muted)' }}>السائق في الطريق — ستصلك مياهك قريباً</p>
          </div>
        </div>
      )}

      {/* ══════ MARKETPLACE OFFERS BANNER ══════ */}
      {isMarketplace && (
        <div className="rounded-2xl overflow-hidden"
          style={{ border: '1.5px solid rgba(10,186,181,0.35)', background: 'rgba(10,186,181,0.06)' }}>
          <div className="px-4 py-4 flex items-start gap-3">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(10,186,181,0.12)', border: '1px solid rgba(10,186,181,0.2)' }}>
              {order.status === 'offers_ready'
                ? <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={1.8}><path strokeLinecap="round" d="M9 14.25l1.5 1.5 4.5-4.5M12 3v1M3 12h1M20 12h1M5.636 5.636l.707.707M18.364 5.636l-.707.707"/><circle cx="12" cy="12" r="6"/></svg>
                : <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={1.8}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 6v6l4 2"/></svg>
              }
            </span>
            <div className="flex-1">
              <p className="font-black text-sm mb-1" style={{ color: '#0ABAB5' }}>
                {order.status === 'offers_ready'
                  ? 'اكتملت فترة التجميع — هذه جميع العروض!'
                  : order.offer_expires_at
                    ? `جاري جمع عروض المزودين${bidCountdown !== null ? ` — تنتهي خلال ${bidCountdown}ث` : '...'}`
                    : 'في انتظار عروض المزودين...'}
              </p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--wt-text-muted)' }}>
                {order.status === 'offers_ready'
                  ? 'قارن الأسعار واختر العرض الأنسب لك'
                  : order.offer_expires_at
                    ? 'يرسل كل سائق عرضه — ستظهر العروض بعد انتهاء المهلة'
                    : 'تم إرسال طلبك للسائقين المتاحين'}
              </p>
              {order.status === 'collecting_offers' && order.offer_expires_at && (
                <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(10,186,181,0.2)' }}>
                  <BidProgressBar expiresAt={order.offer_expires_at} />
                </div>
              )}
            </div>
          </div>
          {order.status === 'offers_ready' && (
            <div className="px-4 pb-4">
              <button
                onClick={() => navigate(`/customer/orders/${id}/offers`)}
                className="w-full py-3 rounded-xl text-white font-black text-sm flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #0ABAB5, #0066CC)', boxShadow: '0 4px 16px rgba(10,186,181,0.3)' }}
              >
                🏷️ عرض جميع العروض واختر الأنسب
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══════ EXPIRED NO OFFERS BANNER ══════ */}
      {order.status === 'expired_no_offers' && !adjustDone && (
        <div className="rounded-2xl overflow-hidden" style={{ border: '1.5px solid rgba(244,63,94,0.3)' }}>
          <div className="px-4 py-3.5 flex items-start gap-3" style={{ background: 'rgba(244,63,94,0.08)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
              style={{ background: 'rgba(244,63,94,0.12)', border: '1px solid rgba(244,63,94,0.2)' }}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth={1.8}><circle cx="12" cy="12" r="10"/><path strokeLinecap="round" d="M12 6v6l4 2"/><path strokeLinecap="round" d="M15 17l1.5 1.5"/></svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: '#f43f5e' }}>انتهت مهلة استقبال العروض</p>
              <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--wt-text-muted)' }}>
                {altLoading
                  ? 'جاري البحث عن بدائل متاحة...'
                  : alternatives && alternatives.length > 0
                    ? 'يتوفر مزودون بكميات مختلفة — اختر بديلاً أو ألغِ الطلب'
                    : 'لا يوجد مزودون متاحون حالياً — يمكنك إلغاء الطلب أو الانتظار'}
              </p>
            </div>
          </div>
          {altLoading && (
            <div className="px-4 py-4 flex items-center gap-2" style={{ background: 'var(--wt-surface)' }}>
              <div className="w-4 h-4 border-2 rounded-full animate-spin"
                style={{ borderColor: 'rgba(244,63,94,0.2)', borderTopColor: '#f43f5e' }} />
              <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>جاري تحميل الخيارات المتاحة...</p>
            </div>
          )}
          {!altLoading && alternatives && alternatives.length > 0 && (
            <div style={{ background: 'var(--wt-surface)' }}>
              {alternatives.map((alt, i) => (
                <div key={alt.capacity_tons} className="px-4 py-3.5 flex items-center gap-3"
                  style={{ borderTop: i === 0 ? '1px solid var(--wt-border)' : 'none', borderBottom: '1px solid var(--wt-border)' }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>{alt.capacity_tons} طن</span>
                      {alt.capacity_tons < (order.quantity || 0) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>أصغر</span>
                      )}
                    </div>
                    {alt.total !== null && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--wt-text-muted)' }}>
                        التكلفة: <span className="font-bold" style={{ color: 'var(--wt-text-primary)' }}>{Number(alt.total).toFixed(2)} <SAR className="h-[0.85em] w-auto inline" /></span>
                      </p>
                    )}
                  </div>
                  <button onClick={() => acceptAlternative(alt.capacity_tons)} disabled={adjusting}
                    className="px-3.5 py-2 rounded-xl text-white text-xs font-bold active:scale-95 transition-all disabled:opacity-50 shrink-0"
                    style={{ background: 'linear-gradient(135deg, #0ABAB5, #0066CC)' }}>
                    {adjusting ? '...' : 'قبول'}
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="px-4 py-3 flex items-center justify-between gap-3"
            style={{ background: 'var(--wt-bg-2)', borderTop: '1px solid var(--wt-border)' }}>
            <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>يمكنك إلغاء الطلب أو الانتظار</p>
            <button onClick={() => load(true)} className="text-xs font-bold hover:underline shrink-0" style={{ color: '#0ABAB5' }}>
              تحديث
            </button>
          </div>
        </div>
      )}

      {/* ══════ SMART NO DRIVER BANNER ══════ */}
      {order.status === 'no_driver_available' && !adjustDone && (
        <div className="rounded-2xl overflow-hidden" style={{ border: '1.5px solid rgba(251,191,36,0.35)' }}>
          <div className="px-4 py-3.5 flex items-start gap-3" style={{ background: 'rgba(251,191,36,0.08)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
              style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)' }}>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#fbbf24' }}><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: '#fbbf24' }}>
                لا تتوفر شاحنة بسعة {order.quantity || ''} طن حالياً
              </p>
              <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--wt-text-muted)' }}>
                {altLoading
                  ? 'جاري البحث عن بدائل متاحة...'
                  : alternatives && alternatives.length > 0
                    ? 'يتوفر لدينا شاحنات بأحجام مختلفة — اختر ما يناسبك'
                    : 'لا توجد شاحنات متاحة حالياً — سيتم التعيين يدوياً'
                }
              </p>
            </div>
          </div>

          {altLoading && (
            <div className="px-4 py-4 flex items-center gap-2" style={{ background: 'var(--wt-surface)' }}>
              <div className="w-4 h-4 border-2 rounded-full animate-spin"
                style={{ borderColor: 'rgba(251,191,36,0.2)', borderTopColor: '#fbbf24' }} />
              <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>جاري تحميل الخيارات المتاحة...</p>
            </div>
          )}

          {!altLoading && alternatives && alternatives.length > 0 && (
            <div style={{ background: 'var(--wt-surface)' }}>
              {alternatives.map((alt, i) => (
                <div key={alt.capacity_tons} className="px-4 py-3.5 flex items-center gap-3"
                  style={{ borderTop: i === 0 ? '1px solid var(--wt-border)' : 'none', borderBottom: '1px solid var(--wt-border)' }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>{alt.capacity_tons} طن</span>
                      {alt.capacity_tons < (order.quantity || 0) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>أصغر</span>
                      )}
                      {alt.capacity_tons > (order.quantity || 0) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{ background: 'rgba(10,186,181,0.12)', color: '#0ABAB5', border: '1px solid rgba(10,186,181,0.3)' }}>أكبر</span>
                      )}
                    </div>
                    {alt.total !== null && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--wt-text-muted)' }}>
                        الإجمالي:{' '}
                        <span className="font-bold" style={{ color: 'var(--wt-text-primary)' }}>{Number(alt.total).toFixed(2)}{' '}<SAR className="h-[0.85em] w-auto" /></span>
                        {alt.price_per_ton && (
                          <span style={{ color: 'var(--wt-text-muted)' }}> ({Number(alt.price_per_ton).toFixed(2)}/طن)</span>
                        )}
                      </p>
                    )}
                  </div>
                  <button onClick={() => acceptAlternative(alt.capacity_tons)} disabled={adjusting}
                    className="px-3.5 py-2 rounded-xl text-white text-xs font-bold active:scale-95 transition-all disabled:opacity-50 shrink-0"
                    style={{ background: 'linear-gradient(135deg, #0ABAB5, #0066CC)' }}>
                    {adjusting ? '...' : 'قبول'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {!altLoading && (
            <div className="px-4 py-3 flex items-center justify-between gap-3"
              style={{ background: 'var(--wt-bg-2)', borderTop: '1px solid var(--wt-border)' }}>
              <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>أو انتظر وصول سائق للحجم المطلوب ({order.quantity} طن)</p>
              <button onClick={() => load(true)} className="text-xs font-bold hover:underline shrink-0" style={{ color: '#0ABAB5' }}>
                تحديث الحالة
              </button>
            </div>
          )}
        </div>
      )}

      {/* Adjust success state */}
      {adjustDone && (
        <div className="rounded-2xl px-4 py-3.5 flex items-center gap-3"
          style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.15)' }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
          </span>
          <div>
            <p className="text-sm font-bold" style={{ color: '#10b981' }}>تم تعديل الطلب بنجاح</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--wt-text-muted)' }}>جاري البحث عن سائق متاح للحجم الجديد...</p>
          </div>
        </div>
      )}

      {/* ══════ STATUS TIMELINE BAR ══════ */}
      {order.status !== 'cancelled' && order.status !== 'no_driver_available' && (
        <div className="rounded-2xl p-4" style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)' }}>
          <div className="flex items-start justify-between relative">
            <div className="absolute top-4 right-4 left-4 h-0.5 rounded-full" style={{ background: 'var(--wt-border)' }} />
            <div
              className="absolute top-4 right-4 h-0.5 rounded-full transition-all duration-700"
              style={{
                background: 'linear-gradient(90deg, #0ABAB5, #0066CC)',
                width: `${(Math.max(0, currentIdx) / (timelineSteps.length - 1)) * (100 - 10)}%`,
              }}
            />
            {timelineSteps.map((step) => (
              <div key={step.status} className="flex flex-col items-center gap-1.5 relative z-10 flex-1">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm transition-all duration-300"
                  style={{
                    background: step.done
                      ? 'linear-gradient(135deg, #0ABAB5, #0066CC)'
                      : step.active
                      ? 'rgba(10,186,181,0.15)'
                      : 'var(--wt-bg-2)',
                    border: step.done || step.active ? '1.5px solid #0ABAB5' : '1px solid var(--wt-border)',
                    color: step.done ? '#fff' : step.active ? '#0ABAB5' : 'var(--wt-text-muted)',
                    boxShadow: step.active ? '0 0 10px rgba(10,186,181,0.3)' : 'none',
                  }}
                >
                  {step.done && !step.active ? (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                  ) : STEP_ICONS[step.status]}
                </div>
                <p className="text-center leading-tight" style={{ fontSize: '9px', maxWidth: '44px', color: step.done ? 'var(--wt-text-secondary)' : 'var(--wt-text-muted)' }}>
                  {step.label}
                </p>
                {step.time && (
                  <p className="text-center" style={{ fontSize: '8px', color: 'var(--wt-text-muted)' }}>
                    {new Date(step.time).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════ TRACKING MAP ══════ */}
      {(isActive || isCompleted) && (driverPos || destPos) && (
        <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)' }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--wt-border)' }}>
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--wt-text-primary)' }}>
              {isActive ? (
                <><span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#0ABAB5', boxShadow: '0 0 6px rgba(10,186,181,0.6)' }} />تتبع الطلب مباشرة</>
              ) : '📍 موقع التوصيل'}
            </h3>
            {isActive && (
              <button onClick={() => load(true)} className="text-xs font-bold hover:underline" style={{ color: '#0ABAB5' }}>
                تحديث
              </button>
            )}
          </div>
          <GoogleMapTracker
            driverPos={driverPos}
            destPos={destPos}
            height={240}
          />
          {order.driver_name && isActive && (
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'var(--wt-bg-2)' }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-black text-white"
                  style={{ background: 'linear-gradient(135deg, #0ABAB5, #0066CC)' }}>
                  {order.driver_name[0]}
                </div>
                <div>
                  <p className="text-xs font-bold" style={{ color: 'var(--wt-text-primary)' }}>{order.driver_name}</p>
                  {order.eta_minutes > 0 && (
                    <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>متوقع الوصول خلال ~{order.eta_minutes} دقيقة</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {order.driver_phone && (
                  <a href={`tel:${order.driver_phone}`}
                    className="w-9 h-9 rounded-xl flex items-center justify-center transition-all"
                    style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)' }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  </a>
                )}
                <button
                  onClick={() => navigate(`/customer/chat/${id}`)}
                  className="w-12 h-12 rounded-xl flex items-center justify-center transition-all"
                  style={{ background: 'rgba(10,186,181,0.15)', color: '#0ABAB5', border: '1px solid rgba(10,186,181,0.25)' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════ PRICE CARD ══════ */}
      {(estTotal > 0 || actTotal > 0) && (
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--wt-border)' }}>
          <div className="px-5 pt-4 pb-3 text-center"
            style={{ background: 'linear-gradient(135deg, #0a2540, #0d3060 50%, #0ABAB5)' }}>
            <p className="text-white/60 text-[10px] font-medium tracking-widest uppercase mb-0.5">
              فاتورة ضريبية {isCompleted ? 'نهائية' : 'مبدئية'}
            </p>
            <p className="text-white font-bold text-sm">{order.water_type || 'مياه'} — {order.quantity} طن</p>
            {order.payment_status === 'paid' && (
              <span className="inline-block mt-1.5 text-[10px] bg-white/20 text-white px-2.5 py-0.5 rounded-full font-semibold">✓ مدفوع</span>
            )}
          </div>
          {(() => {
            const baseTotal  = isCompleted ? (actTotal || estTotal) : estTotal;
            const discount   = order.discount_amount || 0;
            const tax        = order.tax_total != null ? order.tax_total : 0;
            const preTax     = Math.max(0, baseTotal - tax);
            const finalTotal = Math.max(0, baseTotal - discount);
            return (
              <div className="px-5 pt-4 pb-5 space-y-3" style={{ background: 'var(--wt-surface)' }}>
                {[['السعر', preTax], ['ضريبة القيمة المضافة (15%)', tax]].map(([label, val]) => (
                  <div key={label} className="flex justify-between items-center text-sm">
                    <span style={{ color: 'var(--wt-text-muted)' }}>{label}</span>
                    <span className="font-semibold" style={{ color: 'var(--wt-text-primary)' }}>{fmtSAR(val)}{' '}<SAR className="h-[0.85em] w-auto" /></span>
                  </div>
                ))}
                {discount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-medium flex items-center gap-1" style={{ color: '#10b981' }}>🎉 خصم {order.coupon_code && <span className="font-mono text-xs px-1.5 rounded" style={{ background: 'rgba(16,185,129,0.15)' }}>({order.coupon_code})</span>}</span>
                    <span className="font-bold" style={{ color: '#10b981' }}>-{fmtSAR(discount)}{' '}<SAR className="h-[0.85em] w-auto" /></span>
                  </div>
                )}
                <div className="pt-3 flex justify-between items-center" style={{ borderTop: '2px dashed var(--wt-border)' }}>
                  <span className="font-bold text-sm" style={{ color: 'var(--wt-text-secondary)' }}>الإجمالي</span>
                  <span className="text-2xl font-black" style={{ color: '#0ABAB5' }}>{fmtSAR(finalTotal)}{' '}<SAR className="h-[0.75em] w-auto" /></span>
                </div>
                {!isCompleted && estTotal > 0 && (
                  <p className="text-[10px] text-center pt-0.5" style={{ color: 'var(--wt-text-muted)' }}>⚠ السعر تقديري وقد يختلف عند التنفيذ</p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ══════ COUPON SECTION ══════ */}
      {!isCompleted && order.status !== 'cancelled' && (
        <div className="rounded-2xl p-4 space-y-3" style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)' }}>

          {suggestedCoupon && !order.coupon_code && (
            <div className="rounded-xl p-3.5" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.25)' }}>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(139,92,246,0.12)' }}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth={1.8}><path strokeLinecap="round" d="M20 12v10H4V12M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
                </span>
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: '#a78bfa' }}>لديك كوبون خصم مجاني!</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--wt-text-muted)' }}>
                    {suggestedCoupon.discount_type === 'percentage'
                      ? `خصم ${suggestedCoupon.discount_value}% على طلبك`
                      : `خصم ${Number(suggestedCoupon.discount_amount).toFixed(0)} `}
                    {suggestedCoupon.discount_type !== 'percentage' && <SAR style={{ width: '0.8em', height: '0.8em', display: 'inline' }} />}
                  </p>
                  <button
                    onClick={() => applyCoupon(suggestedCoupon.code)}
                    disabled={couponApplying}
                    className="mt-2.5 inline-flex items-center gap-2 text-white text-xs font-bold px-3.5 py-1.5 rounded-lg active:scale-95 transition-all disabled:opacity-50"
                    style={{ background: 'rgba(139,92,246,0.8)' }}
                  >
                    <span className="font-mono tracking-widest">{suggestedCoupon.code}</span>
                    <span>{couponApplying ? '...' : '← تطبيق الآن'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {order.coupon_code ? (
            <div className="flex items-center gap-3 rounded-xl px-3.5 py-3"
              style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
              <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.12)' }}>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={1.8}><path strokeLinecap="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: '#10b981' }}>كوبون مطبّق</p>
                <p className="text-xs mt-0.5 font-mono tracking-widest" style={{ color: 'var(--wt-text-muted)' }}>{order.coupon_code}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-black" style={{ color: '#10b981' }}>-{fmtSAR(order.discount_amount)}</p>
                <button onClick={removeCoupon} disabled={couponRemoving}
                  className="text-[11px] font-medium mt-0.5 transition-colors" style={{ color: '#f43f5e' }}>
                  {couponRemoving ? '...' : 'إزالة'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <h3 className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>كوبون الخصم</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={e => setCouponInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
                  placeholder="أدخل كود الكوبون"
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm text-center font-mono tracking-widest focus:outline-none transition-all"
                  style={{ background: 'var(--wt-bg-2)', border: '1px solid var(--wt-border)', color: 'var(--wt-text-primary)' }}
                />
                <button
                  onClick={() => applyCoupon(couponInput)}
                  disabled={!couponInput.trim() || couponApplying}
                  className="px-4 py-2.5 rounded-xl text-white text-sm font-bold active:scale-95 transition-all disabled:opacity-40 shrink-0"
                  style={{ background: 'linear-gradient(135deg, #0ABAB5, #0066CC)' }}
                >
                  {couponApplying ? '...' : 'تطبيق'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════ PAYMENT METHOD ══════ */}
      {(estTotal > 0 || actTotal > 0) && order.status !== 'cancelled' && (() => {
        const orderTotal      = estTotal || actTotal || 0;
        const walletSufficient = walletBalance !== null && walletBalance >= orderTotal;
        return (
          <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)' }}>
            <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--wt-border)' }}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={2}><rect x="2" y="5" width="20" height="14" rx="2"/><path strokeLinecap="round" d="M2 10h20"/></svg>
              <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>طريقة الدفع</p>
            </div>
            <div className="p-3 space-y-2">
              {/* نقداً */}
              {(() => {
                const sel = payMethod === 'cash';
                return (
                  <button type="button" onClick={() => !isCompleted && savePayment('cash')}
                    disabled={isCompleted || savingPay}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-right disabled:opacity-50"
                    style={{ background: sel ? 'rgba(10,186,181,0.08)' : 'var(--wt-bg-2)', border: sel ? '1.5px solid rgba(10,186,181,0.5)' : '1px solid var(--wt-border)' }}>
                    <span style={{ color: sel ? '#0ABAB5' : 'var(--wt-text-muted)', display:'flex' }}>{getPmIcon('cash', isDark)}</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>نقداً</p>
                      <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>يُسلّم للسائق عند التسليم</p>
                    </div>
                    {sel && <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>}
                  </button>
                );
              })()}
              {/* دفع إلكتروني */}
              {(() => {
                const sel = payMethod === 'card';
                return (
                  <button type="button" onClick={() => !isCompleted && savePayment('card')}
                    disabled={isCompleted || savingPay}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-right disabled:opacity-50"
                    style={{ background: sel ? 'rgba(10,186,181,0.08)' : 'var(--wt-bg-2)', border: sel ? '1.5px solid rgba(10,186,181,0.5)' : '1px solid var(--wt-border)' }}>
                    <span style={{ color: sel ? '#0ABAB5' : 'var(--wt-text-muted)', display:'flex' }}>{getPmIcon('card', isDark)}</span>
                    <div className="flex-1">
                      <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>دفع إلكتروني</p>
                      <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>Visa · Mastercard · مدى · Apple Pay</p>
                    </div>
                    {sel && <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>}
                  </button>
                );
              })()}
              {/* المحفظة */}
              {(() => {
                const sel = payMethod === 'wallet';
                return (
                  <button type="button"
                    onClick={() => { if (isCompleted || savingPay) return; walletSufficient ? savePayment('wallet') : setCheckoutOpen(true); }}
                    disabled={isCompleted || savingPay}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-right disabled:opacity-50"
                    style={{ background: sel ? 'rgba(16,185,129,0.08)' : 'var(--wt-bg-2)', border: sel ? '1.5px solid rgba(16,185,129,0.5)' : '1px solid var(--wt-border)' }}>
                    {getPmIcon('wallet', isDark)}
                    <div className="flex-1">
                      <p className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>المحفظة</p>
                      <p className="text-xs" style={{ color: !walletSufficient && walletBalance !== null ? '#fbbf24' : 'var(--wt-text-muted)' }}>
                        {walletBalance !== null ? <>الرصيد: {Number(walletBalance).toFixed(2)} <SAR className="h-[0.85em] w-auto" /></> : 'جاري تحميل...'}
                        {walletBalance !== null && !walletSufficient && ' — غير كافٍ'}
                      </p>
                    </div>
                    {sel
                      ? <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
                      : !walletSufficient && walletBalance !== null
                        ? <span className="text-xs font-bold px-2 py-0.5 rounded-lg shrink-0" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>شحن</span>
                        : null}
                  </button>
                );
              })()}
            </div>
            {payMethod && (
              <p className="text-xs rounded-xl px-3 py-2" style={{ background: 'var(--wt-bg-2)', color: 'var(--wt-text-muted)', border: '1px solid var(--wt-border)' }}>
                {payMethod === 'wallet'
                  ? <>✓ سيُخصم {orderTotal ? <>{Number(orderTotal).toFixed(2)}{' '}<SAR className="h-[0.85em] w-auto" /></> : 'المبلغ'} من رصيد محفظتك</>
                  : payMethod === 'card'
                    ? 'سيتم تحويلك لبوابة الدفع الآمنة عند إتمام الطلب'
                    : 'يُسلَّم للسائق نقداً عند التسليم'}
              </p>
            )}
          </div>
        );
      })()}

      {/* ══════ ORDER DETAILS ══════ */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--wt-surface)', border: '1px solid var(--wt-border)' }}>
        <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--wt-border)' }}>
          <h3 className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>تفاصيل الطلب</h3>
        </div>
        {[
          { label: 'نوع المياه',      value: order.water_type },
          { label: 'الكمية المطلوبة', value: `${order.quantity} طن` },
          order.delivered_qty > 0 && { label: 'الكمية المُسلّمة', value: `${order.delivered_qty} طن` },
          { label: 'عنوان التوصيل',   value: order.delivery_address || order.site_name },
          order.ship_name && { label: 'المحطة', value: order.ship_name },
          order.customer_notes && { label: 'ملاحظاتك', value: order.customer_notes },
          order.driver_notes && { label: 'ملاحظات السائق', value: order.driver_notes },
          { label: 'تاريخ الطلب', value: fmtDate(order.created_at) },
          order.completed_at && { label: 'وقت التسليم', value: fmtDate(order.completed_at) },
        ].filter(Boolean).map(({ label, value }, i, arr) => (
          <div key={label} className="flex items-start justify-between gap-3 px-4 py-3"
            style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--wt-border)' : 'none' }}>
            <span className="text-xs shrink-0" style={{ color: 'var(--wt-text-muted)' }}>{label}</span>
            <span className="text-sm font-medium text-left" style={{ color: 'var(--wt-text-primary)' }}>{value}</span>
          </div>
        ))}
      </div>

      {/* ══════ RATING ══════ */}
      {canRate && (
        <div className="rounded-2xl p-4 space-y-3" style={{ background: 'var(--wt-surface)', border: '1.5px solid rgba(10,186,181,0.3)', boxShadow: '0 0 20px rgba(10,186,181,0.1)' }}>
          <h3 className="text-sm font-bold" style={{ color: 'var(--wt-text-primary)' }}>قيّم تجربتك</h3>
          <p className="text-xs" style={{ color: 'var(--wt-text-muted)' }}>تقييمك يساعدنا على تحسين الخدمة</p>
          <div className="flex gap-3 justify-center py-1">
            {[1,2,3,4,5].map(s => (
              <button key={s} onClick={() => setRating(s)}
                className="text-4xl transition-all active:scale-125"
                style={{ color: s <= rating ? '#FBBF24' : 'var(--wt-border)', filter: s <= rating ? 'drop-shadow(0 0 4px rgba(251,191,36,0.5))' : 'none' }}>
                ★
              </button>
            ))}
          </div>
          <textarea
            value={review}
            onChange={e => setReview(e.target.value)}
            placeholder="أضف تعليقاً (اختياري)..."
            rows={2}
            className="w-full px-3 py-2.5 rounded-xl text-sm resize-none focus:outline-none transition-all"
            style={{ background: 'var(--wt-bg-2)', border: '1px solid var(--wt-border)', color: 'var(--wt-text-primary)' }}
          />
          <button
            onClick={submitRating}
            disabled={!rating || ratingLoading}
            className="w-full text-white font-bold py-3 rounded-xl transition-all disabled:opacity-50 text-sm"
            style={{ background: 'linear-gradient(135deg, #0ABAB5, #0066CC)', boxShadow: '0 4px 16px rgba(10,186,181,0.3)' }}
          >
            {ratingLoading ? 'جاري الإرسال...' : 'إرسال التقييم'}
          </button>
        </div>
      )}

      {order.customer_rating && (
        <div className="rounded-2xl p-4 text-center" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <div className="text-3xl mb-1" style={{ color: '#FBBF24' }}>{'★'.repeat(order.customer_rating)}<span style={{ color: 'var(--wt-border)' }}>{'☆'.repeat(5 - order.customer_rating)}</span></div>
          <p className="text-sm font-bold" style={{ color: '#10b981' }}>شكراً على تقييمك</p>
          {order.customer_review && <p className="text-xs mt-1 italic" style={{ color: 'var(--wt-text-muted)' }}>"{order.customer_review}"</p>}
        </div>
      )}


      {order.status === 'cancelled' && (
        <div className="rounded-2xl p-4 text-center" style={{ background: 'rgba(244,63,94,0.06)', border: '1px solid rgba(244,63,94,0.25)' }}>
          <p className="text-sm font-bold" style={{ color: '#f43f5e' }}>تم إلغاء هذا الطلب</p>
          {order.cancel_reason && <p className="text-xs mt-1" style={{ color: 'var(--wt-text-muted)' }}>{order.cancel_reason}</p>}
        </div>
      )}

      <CheckoutSheet
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={(newBal) => {
          setCheckoutOpen(false);
          if (newBal !== undefined) setWalletBalance(newBal);
        }}
      />
    </div>
  );
}

